import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { ROUTE_URLS } from '@routing/route-urls.const';
import { catchError, Observable, of, take } from 'rxjs';
import { TimerService } from '@core/services/system/timer/timer.service';

type NextModule = {
  name: string;
  nextPages: string[];
  load: () => Observable<any>;
  isLoad: boolean;
};

@Injectable()
export class PreloadNextModules implements PreloadingStrategy {
  private pages: Record<string, NextModule> = {};
  private activeTimeoutId: number | null = null;
  constructor(private timerService: TimerService) {}

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if (route?.data?.name && !this.pages?.[route.data.name]) {
      this.pages[route.data.name] = {
        name: route.data.name,
        load,
        nextPages: route.data?.nextPages || [],
        isLoad: false,
      };
    }
    return route?.data?.preload ? load() : of(null);
  }

  preloadNextPages(currentPage: string, timeout: number = 500): void {

    if (this.activeTimeoutId) {
      clearTimeout(this.activeTimeoutId);
    }


    this.activeTimeoutId = this.timerService.setTimeout(() => {
      this.preloadCurrentPage(currentPage);
      this.activeTimeoutId = null;
    }, timeout);
  }

  private preloadCurrentPage(currentPage: string): void {
    if (currentPage === ROUTE_URLS.employee && !this.pages[currentPage]) {
      this.pages[currentPage] = {
        name: ROUTE_URLS.employee,
        nextPages: [ROUTE_URLS.collection, ROUTE_URLS.checkout, ROUTE_URLS.invoices],
        load: () => of(null),
        isLoad: false,
      };
    }

    const route = this.pages[currentPage];
    if (!route?.nextPages?.length) return;

    const unloadPages = route.nextPages
      .map((name) => this.pages[name])
      .filter((r) => r && !r.isLoad);

    if (!unloadPages.length) return;

    unloadPages.forEach((page) => this.loadPage(page));
    this.pages[currentPage].isLoad = true;
  }

  private loadPage(route: NextModule): void {
    if (route && !route.isLoad) {
      route.isLoad = true;

      route.load()
        .pipe(
          take(1),
          catchError((err) => {
            return of(null);
          })
        )
        .subscribe({
          complete: () => {
            route.load = () => of(null);
          }
        });
    }
  }
}
